/**
 * Brief 26a - Policy Reader (read-only)
 *
 * Renders the policy header, current version metadata, body markdown,
 * sections panel, and outstanding-CR list for one policy slug.
 *
 * Scope: read-only. No edit/write affordances. Policy body and section bodies
 * are rendered through the shared MarkdownDoc surface (utils/markdownDoc.js)
 * so headings, tables and lists format instead of showing raw Markdown.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { authFetch } from "../../config/api";
import { ownerRoleLabel } from "./policyVocab";
import TierBadge from "./TierBadge";
import MarkdownDoc from "../../components/MarkdownDoc";
import {
  ArrowLeft, AlertCircle, RefreshCw, BookOpen,
  CheckCircle2, AlertTriangle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function PolicyReader() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ack state for the calling user against this policy
  const [myAck, setMyAck] = useState(null); // { ack_status, attested_version_label, attested_at, needs_ack }
  const [ackSubmitting, setAckSubmitting] = useState(false);
  const [ackError, setAckError] = useState(null);

  const loadMyAck = useCallback(async () => {
    if (!slug) return;
    try {
      const r = await authFetch(`${API}/api/policy/my/library`, { credentials: "include" });
      if (!r.ok) return;
      const j = await r.json();
      const row = (j.policies || []).find((p) => p.slug === slug);
      if (row) {
        setMyAck({
          ack_status: row.ack_status,
          attested_version_label: row.attested_version_label,
          attested_at: row.attested_at,
          needs_ack: row.needs_ack,
        });
      }
    } catch { /* soft-fail — footer just won't show */ }
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await authFetch(`${API}/api/policy/${encodeURIComponent(slug)}`, {
          credentials: "include",
        });
        if (r.status === 404) throw new Error("Policy not found");
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    loadMyAck();
    return () => { cancelled = true; };
  }, [slug, loadMyAck]);

  async function submitAck() {
    if (!data || !data.version || !data.version._id) return;
    setAckSubmitting(true);
    setAckError(null);
    try {
      const r = await authFetch(`${API}/api/policy/${encodeURIComponent(slug)}/attest`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          policy_version_id: data.version._id,
          signature_body: "I have read and understood this policy.",
          reason: "self-service acknowledgement",
        }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      await loadMyAck();
    } catch (e) {
      setAckError(e.message || String(e));
    } finally {
      setAckSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div data-testid="policy-reader-loading" className="p-6">
        <RefreshCw className="inline-block animate-spin mr-2" size={16} />
        Loading {slug}...
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="policy-reader-error" className="p-6 text-red-400">
        <AlertCircle className="inline-block mr-2" size={16} />
        {error}
        <div className="mt-2">
          <Link to="/policy/library" className="text-teal hover:underline">
            <ArrowLeft className="inline-block mr-1" size={14} />
            Back to Policy Library
          </Link>
        </div>
      </div>
    );
  }
  if (!data || !data.policy) {
    return (
      <div data-testid="policy-reader-empty" className="p-6">
        No policy data.
      </div>
    );
  }

  const { policy, version, sections, outstanding_crs } = data;

  return (
    <div data-testid="policy-reader-page" className="p-6 max-w-4xl mx-auto">
      <div className="mb-4">
        <Link to="/policy/library" className="text-sm text-teal hover:underline">
          <ArrowLeft className="inline-block mr-1" size={14} />
          Back to Policy Library
        </Link>
      </div>

      <header className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <BookOpen size={20} />
          <h1 className="text-2xl font-semibold text-white" data-testid="policy-reader-name">
            {policy.name}
          </h1>
          {version && (
            <span
              className="text-sm text-txt-muted"
              data-testid="policy-reader-version-label"
            >
              {version.version_label}
            </span>
          )}
        </div>
        <div className="text-sm text-txt-muted flex flex-wrap gap-3">
          <span data-testid="policy-reader-slug">{policy.slug}</span>
          <span>Owner: {ownerRoleLabel(policy.owner_role)}</span>
          <span>
            Tier:{" "}
            <TierBadge tier={policy.tier} testId="policy-reader-tier" />
          </span>
          <span data-testid="policy-reader-anchor">
            Anchor: {policy.regulatory_anchor}
          </span>
        </div>
      </header>

      {version && (
        <section className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Body</h2>
          <MarkdownDoc
            markdown={version.body_markdown}
            testId="policy-reader-body"
            className="rounded-md border border-navy-border bg-navy-card p-6 shadow-lg"
          />
        </section>
      )}

      <section className="mb-6">
        <h2 className="text-lg font-semibold mb-2">
          Sections ({sections.length})
        </h2>
        {sections.length === 0 ? (
          <p className="text-sm text-txt-muted">No sections defined.</p>
        ) : (
          <ul className="space-y-2">
            {sections.map((s) => (
              <li
                key={s._id}
                data-testid={`policy-reader-section-${s.section_ref}`}
                className="border border-navy-border rounded p-3"
              >
                <div className="font-mono text-xs text-txt-muted">{s.section_ref}</div>
                <div className="font-medium">{s.heading}</div>
                <MarkdownDoc markdown={s.body_markdown} className="mt-1" />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Acknowledge footer — sticky at the bottom of the reader */}
      {myAck && (
        <div
          data-testid="policy-reader-ack-footer"
          className="sticky bottom-0 left-0 right-0 mt-8 -mx-6 px-6 py-4 border-t bg-navy/95 backdrop-blur border-navy-border"
        >
          <div className="max-w-4xl mx-auto flex flex-wrap items-center gap-3 justify-between">
            <div className="text-sm">
              {myAck.ack_status === "current" && (
                <span
                  data-testid="policy-reader-ack-current"
                  className="inline-flex items-center gap-2 text-emerald-400"
                >
                  <CheckCircle2 size={16} />
                  You acknowledged {myAck.attested_version_label || "this policy"}
                  {myAck.attested_at && " on " + new Date(myAck.attested_at).toLocaleDateString()}
                </span>
              )}
              {myAck.ack_status === "outdated" && (
                <span
                  data-testid="policy-reader-ack-outdated"
                  className="inline-flex items-center gap-2 text-amber-300"
                >
                  <AlertTriangle size={16} />
                  Your previous acknowledgement ({myAck.attested_version_label}) is out of date. Please re-acknowledge the current version.
                </span>
              )}
              {myAck.ack_status === "never" && (
                <span
                  data-testid="policy-reader-ack-never"
                  className="inline-flex items-center gap-2 text-txt-muted"
                >
                  You have not yet acknowledged this policy.
                </span>
              )}
              {ackError && (
                <div
                  data-testid="policy-reader-ack-error"
                  className="mt-1 text-xs text-red-400"
                >
                  {ackError}
                </div>
              )}
            </div>
            {myAck.needs_ack && version && version._id && (
              <button
                data-testid="policy-reader-ack-button"
                type="button"
                disabled={ackSubmitting}
                onClick={submitAck}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60"
              >
                {ackSubmitting ? (
                  <>
                    <RefreshCw className="animate-spin" size={14} />
                    Recording...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={14} />
                    I have read and understood this policy
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      <section className="mb-6">
        <h2 className="text-lg font-semibold mb-2">
          Outstanding Change Requests ({outstanding_crs.length})
        </h2>
        {outstanding_crs.length === 0 ? (
          <p className="text-sm text-txt-muted">No open CRs for this policy.</p>
        ) : (
          <ul className="space-y-2">
            {outstanding_crs.map((c) => (
              <li
                key={c._id}
                data-testid={`policy-reader-cr-${c.policy_diff && c.policy_diff.missing_artefact_ref}`}
                className="border border-navy-border rounded p-3"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-xs">
                    {c.policy_diff && c.policy_diff.missing_artefact_ref}
                    {/* BM-H3980: a horizon scan change waiting in the change pack */}
                    {c.trigger === "horizon_scan" ? (
                      <Link to="/compliance/change-pack" className="text-teal hover:underline">
                        In the change pack: {(c.scan_items || []).map((s) => s.title).join("; ")}
                      </Link>
                    ) : null}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-900">
                    {c.trigger}
                  </span>
                </div>
                <MarkdownDoc
                  markdown={(c.policy_diff && c.policy_diff.narrative_md) || c.narrative_md || ""}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
